import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const AI_CAPABILITIES = [
  {
    icon: 'ri-brain-line',
    title: 'Machine Learning Models',
    desc: 'Custom-trained ML models that learn from your business data to automate decisions, detect patterns, and generate intelligent predictions at scale.',
    tag: 'Core AI',
    details: ['Supervised & unsupervised learning', 'Custom model training pipelines', 'Model versioning & monitoring', 'Integration with existing data systems'],
  },
  {
    icon: 'ri-chat-ai-line',
    title: 'Conversational AI & Chatbots',
    desc: 'Deploy AI-powered assistants across your platforms that handle customer queries, sales funnels, and internal workflows 24/7 without human intervention.',
    tag: 'NLP',
    details: ['Multi-language support', 'Context-aware dialogue management', 'CRM & helpdesk integration', 'Sentiment analysis & escalation'],
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Predictive Analytics',
    desc: 'Transform raw data into actionable business intelligence with AI-driven forecasting, trend analysis, and anomaly detection customized to your industry.',
    tag: 'Analytics',
    details: ['Demand & revenue forecasting', 'Churn prediction models', 'Real-time anomaly alerts', 'Interactive BI dashboards'],
  },
  {
    icon: 'ri-robot-line',
    title: 'Process Automation (RPA)',
    desc: 'Automate repetitive tasks — data entry, reporting, document routing — with AI-enhanced robotic process automation saving thousands of man-hours annually.',
    tag: 'Automation',
    details: ['Workflow automation design', 'Document parsing & extraction', 'ERP/CRM bot integration', 'Audit trails & compliance logs'],
  },
  {
    icon: 'ri-eye-line',
    title: 'Computer Vision',
    desc: 'Enable machines to interpret and act on visual data — from quality inspection to facial recognition and object detection in real-world environments.',
    tag: 'Vision',
    details: ['Object detection & tracking', 'Quality control automation', 'Document & ID scanning', 'Video analytics & surveillance'],
  },
  {
    icon: 'ri-database-2-line',
    title: 'AI Data Engineering',
    desc: 'Build robust AI-ready data pipelines that cleanse, transform, and structure your raw data so your models always have clean, reliable inputs to work with.',
    tag: 'Data',
    details: ['ETL & data pipeline design', 'Real-time data streaming', 'Feature engineering & storage', 'Data lake & warehouse setup'],
  },
];

const INDUSTRIES_SERVED = [
  { icon: 'ri-hospital-line', name: 'Healthcare' },
  { icon: 'ri-bank-line', name: 'Finance & Banking' },
  { icon: 'ri-store-line', name: 'Retail & eCommerce' },
  { icon: 'ri-ship-line', name: 'Maritime & Logistics' },
  { icon: 'ri-building-2-line', name: 'Manufacturing' },
  { icon: 'ri-government-line', name: 'Government' },
];

export default function AIServicesPage() {
  const featRef = useRef<HTMLElement>(null);
  const featInView = useInView(featRef);
  const indRef = useRef<HTMLElement>(null);
  const indInView = useInView(indRef);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-teal-50/50 blur-3xl translate-y-1/4 -translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Link to="/services" className="text-gray-400 text-sm hover:text-teal-600 transition-colors cursor-pointer">Services</Link>
                <i className="ri-arrow-right-s-line text-gray-300" />
                <span className="text-teal-600 text-sm font-medium">AI Solutions</span>
              </div>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
                AI Solutions
              </span> */}
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
                Intelligence Built Into <span className="text-teal-500">Every Layer</span>
              </h1>
              <p className="text-gray-500 text-xl leading-relaxed mb-8">
                We deliver measurable outcomes through purpose-built intelligent systems. Our AI engineers design, train, and deploy models that solve your specific industry challenges — not generic off-the-shelf tools.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['ISO 9001:2015 Quality', '25 Years Experience', 'Custom AI Models', 'Post-Deployment Support'].map((b) => (
                  <span key={b} className="bg-teal-50 text-teal-700 border border-teal-200 text-xs font-medium px-4 py-1.5 rounded-full">{b}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2">
                  Schedule a Free Consultation <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/portfolio" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-gray-700 font-semibold px-7 py-3.5 rounded-lg border border-gray-200 transition-colors inline-flex items-center gap-2">
                  View Case Studies
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100">
              <div className="w-full h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=artificial%20intelligence%20machine%20learning%20neural%20network%20visualization%20modern%20dashboard%20interface%20glowing%20nodes%20and%20connections%20data%20flow%20analytics%20charts%20clean%20white%20background%20teal%20green%20accent%20colors%20professional%20tech%20software%20interface&width=700&height=500&seq=ai-hero-svc-001&orientation=landscape"
                  alt="AI Solutions"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-teal-600 py-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { val: '120+', label: 'AI Projects Delivered' },
              { val: '98%', label: 'Client Satisfaction' },
              { val: '40+', label: 'AI Engineers' },
              { val: '25+', label: 'Years Experience' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl font-extrabold text-white">{s.val}</div>
                <div className="text-teal-200 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Capabilities */}
        <section ref={featRef} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-14 transition-all duration-700 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Our AI Capabilities</span> */}
              <h2 className="text-4xl font-extrabold text-gray-900 mb-4">End-to-End AI Engineering</h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">From data strategy to production deployment — we own the full AI lifecycle so you can focus on your business.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {AI_CAPABILITIES.map((cap, i) => (
                <div
                  key={cap.title}
                  className={`bg-gray-50 border border-gray-100 rounded-2xl p-7 hover:border-teal-200 hover:bg-teal-50/20 transition-all duration-500 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl flex-shrink-0">
                      <i className={`${cap.icon} text-2xl`} />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2.5 py-0.5 rounded-full">{cap.tag}</span>
                      <h3 className="font-bold text-gray-900 text-base mt-1">{cap.title}</h3>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{cap.desc}</p>
                  <ul className="flex flex-col gap-1.5">
                    {cap.details.map((d) => (
                      <li key={d} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-teal-500 flex-shrink-0" /> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section ref={indRef} className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-12 transition-all duration-700 ${indInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Industries</span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">AI Solutions for Every Sector</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {INDUSTRIES_SERVED.map((ind, i) => (
                <div
                  key={ind.name}
                  className={`bg-white border border-gray-100 rounded-2xl p-6 text-center hover:border-teal-200 transition-all duration-500 ${indInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-600 rounded-xl mx-auto mb-3">
                    <i className={`${ind.icon} text-2xl`} />
                  </div>
                  <div className="text-sm font-medium text-gray-700">{ind.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Our Process</span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">How We Deliver AI Solutions</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { num: '01', icon: 'ri-search-eye-line', title: 'Discovery & Assessment', desc: 'We audit your data assets, identify automation opportunities, and define measurable success metrics.' },
                { num: '02', icon: 'ri-draft-line', title: 'Solution Design', desc: 'Our architects design the AI system architecture, data pipelines, model selection, and integration plan.' },
                { num: '03', icon: 'ri-code-s-slash-line', title: 'Build & Train', desc: 'We develop, train, and rigorously test models against your real data with full documentation.' },
                { num: '04', icon: 'ri-rocket-2-line', title: 'Deploy & Support', desc: 'Production deployment with monitoring dashboards, retraining schedules, and ongoing optimization.' },
              ].map((step) => (
                <div key={step.num} className="relative bg-gray-50 border border-gray-100 rounded-2xl p-7 hover:border-teal-200 transition-colors">
                  <div className="text-6xl font-extrabold text-teal-50 absolute top-4 right-6 leading-none select-none">{step.num}</div>
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-4">
                    <i className={`${step.icon} text-2xl`} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Ready to Integrate AI Into Your Business?</h2>
            <p className="text-teal-100 text-base mb-8">Book a free 30-minute discovery call with our AI architects. ISO 9001:2015 quality. 25 years experience.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-calendar-check-line" /> Get Free Consultation
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
